/** Wire protocol between phones and an online Rang table (a Durable Object). */
import type { DealRecord, HouseRules, Seat, Suit } from '../game/match';
import type { BandhDeal } from '../game/bandh';
import type { Card, HandView } from '../game/rang';
import type { SeatPlayer, Teams } from './schemas';

export interface SeatInfo {
  claimed: boolean;
  online: boolean;
  /** In the voice chat (listening, and talking unless muted). */
  voice: boolean;
  muted: boolean;
}

export type RoomPhase =
  /** Waiting for all four players to take their seats, or for the first-dealer spin. */
  | 'lobby'
  /** Between hands: the dealer shuffles and deals. */
  | 'ready'
  | 'hand'
  /** Every scheduled hand has been played. */
  | 'over';

/** Open Rang (today's game) or Bandh Rang (hidden Rang, ±13/26 signed score, first to ±100 loses). */
export type Variant = 'open' | 'bandh';

export interface BandhView {
  /** The team that dealt first; `score` is read from their side. */
  owner: 'A' | 'B';
  score: number;
  limit: number;
  /** The team dealing (and running) now. */
  dealingTeam: 'A' | 'B';
  deals: BandhDeal[];
  /** Set once the score reaches ±limit. */
  loser: 'A' | 'B' | null;
}

export interface RoomView {
  code: string;
  variant: Variant;
  /** Bandh Rang tables only. */
  bandh: BandhView | null;
  seats: SeatPlayer[];
  teams: Teams;
  rules: HouseRules;
  /** The viewer's seat, or null for a spectator. */
  you: Seat | null;
  isHost: boolean;
  seatInfo: SeatInfo[];
  phase: RoomPhase;
  firstDealer: Seat | null;
  /** Bumps every spin so every phone animates the wheel together. */
  spinId: number;
  deals: DealRecord[];
  /** The hand on the table (also kept after it ends, until the next deal). */
  hand: HandView | null;
  /** Bumps every new deal so phones know to play the shuffle/deal animation. */
  handId: number;
  /** Players share one room: only `speaker`'s phone reads announcements aloud. */
  sameRoom: boolean;
  /** The phone that speaks when `sameRoom` is on: the host's, else the first online player's. */
  speaker: Seat | null;
  /** Who deals next when no hand is being played. */
  nextDealer: Seat | null;
}

export type ClientMsg =
  | { t: 'hello'; clientId: string; hostKey?: string }
  | { t: 'claim'; seat: Seat }
  | { t: 'release'; seat: Seat }
  | { t: 'spin' }
  | { t: 'deal' }
  | { t: 'trump'; suit: Suit }
  /** The caller's first 5 are all below 10: shuffle and deal again. */
  | { t: 'redeal' }
  /** Bandh Rang: on your turn, unable to follow suit — show everyone the hidden Rang. */
  | { t: 'reveal' }
  /** Dealer only, once all cards are out: play the hand, or call it off (the Rang team is saved). */
  | { t: 'decide'; play: boolean }
  | { t: 'play'; card: Card }
  | { t: 'more'; hands: number }
  /** Host only: we're all at one table, so only one phone should speak. */
  | { t: 'sameRoom'; on: boolean }
  | { t: 'voice'; on: boolean; muted: boolean }
  | { t: 'rtc'; to: Seat; data: RtcSignal };

/** WebRTC signalling, relayed untouched (DOM WebRTC types aren't available in the Worker). */
export type RtcSignal = { sdp: { type: 'offer' | 'answer'; sdp?: string } } | { candidate: { candidate?: string; sdpMid?: string | null; sdpMLineIndex?: number | null } };

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export type ServerMsg =
  | { t: 'state'; view: RoomView }
  | { t: 'rtc'; from: Seat; data: RtcSignal }
  | { t: 'error'; message: string };

export interface RoomInfo {
  code: string;
  seats: SeatPlayer[];
  teams: Teams;
}

export interface CreateRoomResponse {
  code: string;
  hostKey: string;
}

/** Keep-alive text the Worker answers without waking the Durable Object. */
export const PING = 'ping';
export const PONG = 'pong';
